import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Select from 'react-select';
import imgUrl from '../assets/images/imgUrl';
import Swal from 'sweetalert2';
import { useNavigate, useLocation } from "react-router";
import { loadStripe } from '@stripe/stripe-js';
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { apiServices } from '../services/apiServices';
import { countryCodes } from '../utils/countryCodes';
import { Configuration } from "../assets/data/config";

const CheckoutForm = ({ onPaymentSuccess, onPaymentError, clientSecret, onPaymentStart, onPaymentComplete }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (elements == null || !clientSecret) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    // Notify parent component that payment has started
    onPaymentStart && onPaymentStart();

    try {
      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message);
        onPaymentError && onPaymentError(submitError.message);
        return;
      }

      // Prevent any redirects and handle payment confirmation
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required' // Prevent automatic redirects
      });

      if (error) {
        setErrorMessage(error.message);
        onPaymentError && onPaymentError(error.message);
      } else {
        console.log('Payment successful!');
        // Verify payment was actually successful
        const paymentIntent = await stripe.retrievePaymentIntent(clientSecret);
        console.log('Payment intent status:', paymentIntent.paymentIntent.status);

        if (paymentIntent.paymentIntent.status === 'succeeded') {
          console.log('Payment confirmed successful!');
          // Call success callback without any page refresh
          onPaymentSuccess && onPaymentSuccess();
        } else {
          console.log('Payment not yet succeeded, status:', paymentIntent.paymentIntent.status);
          setErrorMessage('Payment is being processed. Please wait...');
          onPaymentError && onPaymentError('Payment is being processed. Please wait...');
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      setErrorMessage(errorMessage);
      onPaymentError && onPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
      // Notify parent component that payment processing is complete
      onPaymentComplete && onPaymentComplete();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <Button
        variant="contained"
        sx={{
          mt: 2,
          background: 'linear-gradient(to right, #214F7D, #3A8139)',
          color: 'white',
          borderRadius: "50px"
        }}
        type="submit"
        disabled={!stripe || !elements || isProcessing}>
        {isProcessing ? 'Processing...' : 'Complete Payment & Register'}
      </Button>
      {/* Show error message to your customers */}
      {errorMessage && (
        <Typography color="error" sx={{ mt: 1, fontSize: '14px' }}>
          {errorMessage}
        </Typography>
      )}
    </form>
  );
};

const stripePromise = loadStripe('pk_live_51QSuqmCco9ltycd0RKgPRRacPIukV2k7Eo86CqrHkBh5k3LYYMsSHy7s2tk9gPCZx9Gt9lPYV37mIy1XsDcjt4Tm008KTFmJXN'); //
//const stripePromise = loadStripe('pk_test_51RtjU7KTDUE4cDSIeDRD4296VQ2RMb0C9VL7wZg2rierEt3O9hwpLALOuB3dTSrAlyiMbo8UuqF1qnvwmvenXiDJ00wkfc6EDs');

// Helper function to get base membership amount in cents from localStorage
const getBaseMembershipAmountCents = () => {
  const basePayAmount = localStorage.getItem('base_pay_amount');
  if (basePayAmount) {
    // Convert to cents (multiply by 100)
    return Math.round(parseFloat(basePayAmount) * 100);
  }
  // Fallback to default $21 if not found
  return 21 * 100;
};

const BASE_MEMBERSHIP_AMOUNT_CENTS = 21 * 100; // Fallback default

const Register = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phonenumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [reference, setReference] = useState('') // Peacekeeper ID only (for API)
  const [fullReferralString, setFullReferralString] = useState('') // Full referral string (for display)
  const [dob, setDOB] = useState('')
  const [password, setPassword] = useState('Test@123')
  const [discountPercent, setDiscountPercent] = useState('21%')
  const [country, setCountry] = useState('')
  const [gender, setGender] = useState('')
  const [paymentStatus, setPaymentStatus] = useState('PENDING')
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFullPageLoader, setShowFullPageLoader] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('');
  const [isValidReference, setIsValidReference] = useState(false);
  const [isNoReference, setIsNoReference] = useState(true);
  const [isReferenceFromLink, setIsReferenceFromLink] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isCheckingCredentials, setIsCheckingCredentials] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [imageUploadError, setImageUploadError] = useState('');
  const [mobileEmailVisibility, setMobileEmailVisibility] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Referrer info states
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [isLoadingReferrer, setIsLoadingReferrer] = useState(false);
  const [requiresPayment, setRequiresPayment] = useState(true); // Default to requiring payment
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  // Initialize with value from localStorage or fallback to 21
  const [baseMembershipAmountCents, setBaseMembershipAmountCents] = useState(() => {
    const stored = localStorage.getItem('base_pay_amount');
    if (stored) {
      return Math.round(parseFloat(stored) * 100);
    }
    // Fallback to 21 if not in localStorage (will be updated by API call)
    return 21 * 100;
  });
  const [paymentAmountCents, setPaymentAmountCents] = useState(() => {
    const stored = localStorage.getItem('base_pay_amount');
    if (stored) {
      return Math.round(parseFloat(stored) * 100);
    }
    // Fallback to 21 if not in localStorage (will be updated by API call)
    return 21 * 100;
  });

  // Payment intent states
  const [clientSecret, setClientSecret] = useState('');
  const [stripeOptions, setStripeOptions] = useState(null);
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false);

  // Buffer for console logs to send to save-ui-logs API after registration (users API) success
  const uiLogsRef = useRef([]);

  const navigate = useNavigate();
  const location = useLocation();

  // Capture console.log / warn / error into uiLogsRef for save-ui-logs API
  useEffect(() => {
    const formatArgs = (args) =>
      Array.from(args).map((a) => (typeof a === 'object' && a !== null ? JSON.stringify(a) : String(a))).join(' ');

    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;

    const capture = (level, original) => (...args) => {
      const ts = new Date().toISOString();
      const message = `log_${ts} [${level}] ${formatArgs(args)}`;
      uiLogsRef.current.push(message);
      original.apply(console, args);
    };

    console.log = capture('log', originalLog);
    console.warn = capture('warn', originalWarn);
    console.error = capture('error', originalError);

    return () => {
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
    };
  }, []);

  // Function to fetch base_pay_amount from genericamount API
  const fetchBasePayAmountFromGeneric = async () => {
    try {
      const response = await apiServices.getAPI('genericamount');
      let basePayAmount = null;

      console.log('Full API Response:', response);
      console.log('API Response Data:', response.data);

      // Try to get base_pay_amount from response
      if (response.data) {
        // Check if response.data.data is an array with first element
        if (response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          if (response.data.data[0] && response.data.data[0].base_pay_amount !== undefined) {
            basePayAmount = response.data.data[0].base_pay_amount;
            console.log('Found base_pay_amount in array:', basePayAmount);
          }
        }
        // Check if data is an object with base_pay_amount
        else if (response.data.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data) && response.data.data.base_pay_amount !== undefined) {
          basePayAmount = response.data.data.base_pay_amount;
          console.log('Found base_pay_amount in object:', basePayAmount);
        }
        // Check if base_pay_amount is directly in response.data
        else if (response.data.base_pay_amount !== undefined) {
          basePayAmount = response.data.base_pay_amount;
          console.log('Found base_pay_amount directly:', basePayAmount);
        }
      }

      if (basePayAmount !== null && basePayAmount !== undefined) {
        // Save base_pay_amount to localStorage
        localStorage.setItem('base_pay_amount', basePayAmount.toString());
        // Update state with new amount in cents
        const amountCents = Math.round(parseFloat(basePayAmount) * 100);
        setBaseMembershipAmountCents(amountCents);
        setPaymentAmountCents(amountCents);
        console.log('Base pay amount from genericamount API:', basePayAmount, '($' + (amountCents / 100).toFixed(2) + ')');
      } else {
        console.warn('base_pay_amount not found in API response. Response structure:', response.data);
        // If not found in API, check localStorage or use fallback
        const stored = localStorage.getItem('base_pay_amount');
        if (stored && !localStorage.getItem('base_pay_amount_from_quickinfo')) {
          const storedAmount = Math.round(parseFloat(stored) * 100);
          setBaseMembershipAmountCents(storedAmount);
          setPaymentAmountCents(storedAmount);
          console.log('Using stored base_pay_amount:', stored, '($' + (storedAmount / 100).toFixed(2) + ')');
        } else {
          // Fallback to default
          const fallbackAmount = 21 * 100;
          setBaseMembershipAmountCents(fallbackAmount);
          setPaymentAmountCents(fallbackAmount);
          console.log('Using fallback amount: $21.00');
        }
      }
    } catch (error) {
      console.error('Error fetching base_pay_amount from genericamount:', error);
      // Use localStorage value or fallback if API fails
      const stored = localStorage.getItem('base_pay_amount');
      if (stored && !localStorage.getItem('base_pay_amount_from_quickinfo')) {
        const storedAmount = Math.round(parseFloat(stored) * 100);
        setBaseMembershipAmountCents(storedAmount);
        setPaymentAmountCents(storedAmount);
      } else {
        const fallbackAmount = 21 * 100;
        setBaseMembershipAmountCents(fallbackAmount);
        setPaymentAmountCents(fallbackAmount);
      }
    }
  };

  // Fetch base_pay_amount from genericamount API on component render (fallback if quick-info not called)
  useEffect(() => {
    // Check if base_pay_amount was already set by quick-info API
    const quickInfoAmount = localStorage.getItem('base_pay_amount_from_quickinfo');
    if (quickInfoAmount) {
      console.log('Base pay amount already set from quick-info, skipping genericamount API call');
      return;
    }

    fetchBasePayAmountFromGeneric();
  }, []); // Run only once on component mount

  // Debug effect to track loader state changes
  useEffect(() => {
    console.log('Loader state changed:', { showFullPageLoader, loaderMessage });
  }, [showFullPageLoader, loaderMessage]);

  // Function to create payment intent
  const createPaymentIntent = async () => {
    setIsCreatingPaymentIntent(true);
    setShowFullPageLoader(true);
    setLoaderMessage('Preparing payment...');

    try {
      // Reference number validation check before proceeding to payment
      if (reference && reference.trim() && !isValidReference && !isReferenceFromLink) {
        // If reference is exactly 15 characters or more, validate it
        if (reference.trim().length >= 15) {
          const isValid = await validateManualReference(reference.trim());
          if (!isValid) {
            setIsCreatingPaymentIntent(false);
            setShowFullPageLoader(false);
            setLoaderMessage('');
            return; // Stop here if validation failed
          }
        } else if (reference.trim().length > 0) {
          // Reference entered but not 15 characters yet
          setIsCreatingPaymentIntent(false);
          setShowFullPageLoader(false);
          setLoaderMessage('');
          await Swal.fire({
            title: 'Invalid Reference Number',
            text: 'Reference number must be atleast 15 characters. Please check and try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
          return; // Stop here
        }
      }

      // If reference is entered but validation failed, don't proceed
      if (reference && reference.trim() && !isValidReference && !isReferenceFromLink) {
        setIsCreatingPaymentIntent(false);
        setShowFullPageLoader(false);
        setLoaderMessage('');
        await Swal.fire({
          title: 'Reference Not Validated',
          text: 'Please enter a valid reference number or continue without a referral.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return; // Stop here
      }

      console.log('Creating payment intent...');

      const referralDiscountPercent =
        parseFloat(referrerInfo?.referral_discount_amount) || 0;
      const referralDiscountCents = Math.round(
        baseMembershipAmountCents * (referralDiscountPercent / 100)
      );

      const shouldApplyReferralDiscount =
        (isReferenceFromLink || isValidReference) &&
        reference?.trim() &&
        referralDiscountPercent > 0;

      const calculatedAmountCents = shouldApplyReferralDiscount
        ? Math.max(
          baseMembershipAmountCents - referralDiscountCents,
          0
        )
        : baseMembershipAmountCents;

      setPaymentAmountCents(calculatedAmountCents);

      // Get base_pay_amount from localStorage or use current state
      const basePayAmount = localStorage.getItem('base_pay_amount') || (baseMembershipAmountCents / 100).toString();

      // Try to get latitude/longitude from browser geolocation
      let latitude = '';
      let longitude = '';
      try {
        const position = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) reject(new Error('Geolocation not supported'));
          else navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        latitude = String(position.coords.latitude);
        longitude = String(position.coords.longitude);
      } catch {
        // Leave empty if unavailable or denied
      }

      const response = await apiServices.postAPI('create-payment-intent', {
        amount: calculatedAmountCents,
        currency: 'usd',
        email: email,
        username: name,
        mobile: countryCode + phonenumber,
        country: country,
        dob: dob,
        reference_by: reference,
        base_pay_amount: basePayAmount,
        latitude,
        longitude,
        gender: gender || ''
      });

      console.log('Payment intent response:', response);

      if (!response.data || !response.data.clientSecret) {
        throw new Error('No client secret received from server');
      }

      const { clientSecret: newClientSecret } = response.data;
      setClientSecret(newClientSecret);

      // Set stripe options with clientSecret
      setStripeOptions({
        clientSecret: newClientSecret,
        appearance: {
          /*...*/
        }
      });

      console.log('Payment intent created successfully');

      // Hide loader
      setShowFullPageLoader(false);
      setLoaderMessage('');

      // Open payment modal
      setOpen(true);

    } catch (error) {
      console.error('Error creating payment intent:', error);
      setShowFullPageLoader(false);
      setLoaderMessage('');

      Swal.fire({
        title: 'Payment Setup Failed',
        text: error.message || 'Failed to initialize payment. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsCreatingPaymentIntent(false);
    }
  };

  // Function to handle direct registration without payment (for UNPAID Entity categories)
  const handleDirectRegistration = async () => {
    try {
      setShowFullPageLoader(true);
      setLoaderMessage('Creating Your Account...');
      setIsSubmitting(true);

      console.log('Direct registration (no payment required)');
      console.log('Form data:', { name, email, phonenumber, dob, country, reference });
      console.log('Registration reason: UNPAID Entity category');

      const registrationData = {
        "name": name,
        "phone": countryCode + phonenumber,
        "email": email,
        "password": null,
        "country": country,
        "gender": gender,
        "dob": dob,
        "discount_coupon_percent": discountPercent,
        "reference_by": reference,
        "payment_status": "COMPLIMENTARY", // Free registration for UNPAID category
        "activestatus": "Y",  // New users are active by default
        "mobile_email_flag": mobileEmailVisibility ? "Y" : "N"
      };

      // Add entity discount fields if referrer is Entity
      if (referrerInfo && referrerInfo.usertype === 'Entity') {
        registrationData.entity_discount_percent = referrerInfo.entity_discount_percent || "0";
        registrationData.discount_coupon_percent = referrerInfo.discount_coupon_percent || "0";
        console.log('Entity referral - Adding discount fields:', {
          entity_discount_percent: registrationData.entity_discount_percent,
          discount_coupon_percent: registrationData.discount_coupon_percent
        });
      }

      console.log('Calling registration API with payment status:', paymentStatus, registrationData);

      // Always use FormData since profile image is mandatory
      const formData = new FormData();
      formData.append('profileImage', profileImage);

      // Append other fields to FormData
      Object.keys(registrationData).forEach(key => {
        formData.append(key, registrationData[key]);
      });
      console.log("formData", formData)

      // Save UI logs before users API
      try {
        const logs = uiLogsRef.current.slice();
        if (email && logs.length > 0) {
          await apiServices.postAPI('save-ui-logs', { email: email.trim() || 'unknown', logs });
        }
      } catch (saveLogError) {
        console.error('Failed to save UI logs:', saveLogError);
      }

      console.log('Calling registration API with FormData (includes mandatory profile image)');
      const res = await apiServices.postMultiPartFormDataAPI('users', formData);
      console.log("Registration API Response:", res);

      // Save UI logs after registration success
      try {
        const logs = uiLogsRef.current.slice();
        if (email && logs.length > 0) {
          await apiServices.postAPI('save-ui-logs', { email: email.trim() || 'unknown', logs });
        }
      } catch (saveLogError) {
        console.error('Failed to save UI logs:', saveLogError);
      }

      // Extract peacekeeper ID from response
      const peacekeeperId = res.data?.peacekeeper_id || 'N/A';
      const profileImageInfo = res.data?.profileImage;
      const profileImageStatus = profileImageInfo?.uploaded
        ? `uploaded successfully (${Math.round(profileImageInfo.size / 1024)}KB)`
        : profileImageInfo?.message || 'not provided';

      // Reset form after successful registration
      resetForm();

      // Hide loader
      setShowFullPageLoader(false);
      setLoaderMessage('');
      console.log('Loader hidden after successful registration');

      // Show success message
      await Swal.fire({
        title: '<span style="color: #2e7d32; font-size: 24px; font-weight: 600;">🎉 Registration Successful!</span>',
        html: `
         <div style="text-align: center; margin: 20px 0; font-family: Arial, sans-serif;">
 
  <div style="background: #e8f5e8; border: 2px solid #4caf50; border-radius: 8px; padding: 20px; margin: 15px 0;">
 
    <p style="color: #2e7d32; font-size: 20px; font-weight: 700; margin: 0 0 10px;">
      Welcome to the I Am Peacekeeper Movement!
</p>
 
    <p style="color: #388e3c; font-size: 16px; margin: 0 0 15px;">
      Your account has been created successfully.
</p>
 
    <p style="color: #2e7d32; font-size: 15px; font-weight: 600; margin: 0 0 20px;">
      Your I Am Peacekeeper ID: <strong>${peacekeeperId}</strong>
</p>
 
    <div style="text-align: left; max-width: 500px; margin: 0 auto; font-size: 14px; color: #2e7d32; line-height: 1.6;">
 
      <p style="margin-bottom: 8px;"><strong>Next Steps:</strong></p>
 
      <ul style="padding-left: 18px; margin-top: 0;">
<li>Download the I Am Peacekeeper App from the App Store (<a href="${Configuration.mobileAppiOSUrl}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: underline;">${Configuration.mobileAppiOSUrl}</a>) or Play Store (<a href="${Configuration.mobileAppDownloadUrl}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: underline;">${Configuration.mobileAppDownloadUrl}</a>).</li>
<li>Use your registered email to sign in for the first time.</li>
</ul>
 
      <p style="margin: 12px 0 5px;"><strong>First-Time Login:</strong></p>
 
      <ul style="padding-left: 18px; margin-top: 0;">
<li>Enter your registered email</li>
<li>Receive and verify the OTP</li>
<li>Set your login password</li>
</ul>
 
      <p style="margin: 12px 0 5px;"><strong>Once Logged In:</strong></p>
 
      <ul style="padding-left: 18px; margin-top: 0;">
<li>Your digital ID card will be issued</li>
<li>You will have full access to the I Am Peacekeeper app</li>
<li>You can invite others via your link or QR and begin your Earn While You Serve journey</li>
</ul>
 
    </div>
 
    <p style="color: #555; font-size: 14px; margin-top: 20px;">
      You will now be redirected to the login page to access your account.
</p>
 
  </div>
</div>
        `,
        icon: 'success',
        confirmButtonText: 'Continue to Login',
        showCancelButton: false,
        customClass: {
          popup: 'enhanced-swal-popup',
          confirmButton: 'enhanced-swal-button enhanced-swal-button-success',
          title: 'enhanced-swal-title',
          htmlContainer: 'enhanced-swal-html'
        },
        backdrop: 'rgba(0, 0, 0, 0.4)',
        allowOutsideClick: true,
        timer: undefined
      });

      // Redirect to login
      navigate('/login');

    } catch (error) {
      console.error('Registration error:', error);
      setShowFullPageLoader(false);
      setLoaderMessage('');

      // Show error message
      Swal.fire({
        title: 'Registration Failed',
        text: error.response?.data?.message || error.message || 'An error occurred during registration. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to fetch referrer information
  const fetchReferrerInfo = async (peacekeeperId) => {
    if (!peacekeeperId || !peacekeeperId.trim()) {
      console.log('No PeaceKeeper ID provided for referrer check');
      return;
    }

    setIsLoadingReferrer(true);

    try {
      console.log('Fetching referrer info for:', peacekeeperId);
      const response = await apiServices.getAPI(`user/quick-info/${peacekeeperId.trim()}`);

      if (response.data && response.data.success) {
        const referrerData = response.data.data;
        setReferrerInfo(referrerData);

        console.log('Referrer info received:', referrerData);
        console.log('Usertype:', referrerData.usertype);
        console.log('Category Status:', referrerData.category_status);

        // Extract base_pay_amount from quick-info response if available (takes priority over genericamount)
        if (referrerData.base_pay_amount !== undefined && referrerData.base_pay_amount !== null) {
          const basePayAmount = referrerData.base_pay_amount;
          localStorage.setItem('base_pay_amount', basePayAmount.toString());
          localStorage.setItem('base_pay_amount_from_quickinfo', 'true'); // Flag to indicate quick-info was used
          const amountCents = Math.round(parseFloat(basePayAmount) * 100);
          setBaseMembershipAmountCents(amountCents);
          setPaymentAmountCents(amountCents);
          console.log('Base pay amount from quick-info (priority):', basePayAmount, '($' + (amountCents / 100).toFixed(2) + ')');
        }

        // Only apply special logic for Entity usertypes
        if (referrerData.usertype === 'Entity') {
          // Check if category status is UNPAID
          if (referrerData.category_status === 'UNPAID') {
            setRequiresPayment(false);
            console.log('Entity with UNPAID category - No payment required');
          } else {
            // PAID or null - requires payment (default flow)
            setRequiresPayment(true);
            console.log('Entity with PAID/no category - Payment required');
          }

          // Apply discount if available
          if (referrerData.total_discount_percent) {
            const discount = parseFloat(referrerData.total_discount_percent);
            setAppliedDiscount(discount);
            console.log('Discount applied:', discount + '%');
          }
        } else {
          // Non-entity users - use default payment flow
          setRequiresPayment(true);
          console.log('Non-entity referrer - Default payment flow');
        }
      } else {
        console.log('Failed to fetch referrer info or user not found');
        // Block registration and show popup when quick-info indicates error
        setShowForm(false);
        setIsValidReference(false);
        await Swal.fire({
          title: 'Invalid or Inactive Reference',
          text: 'We could not verify the referrer details. Please check your link or contact your referrer.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error fetching referrer info:', error);
      // Block registration and show popup on API error
      setShowForm(false);
      setIsValidReference(false);
      setIsNoReference(false)
      setIsReferenceFromLink(false);
      // await Swal.fire({
      //   title: 'Reference Check Failed',
      //   text: 'Unable to verify referrer details at the moment. Please try again later or contact your referrer.',
      //   icon: 'error',
      //   confirmButtonText: 'OK'
      // });
    } finally {
      setIsLoadingReferrer(false);
    }
  };

  // Function to validate manually entered reference number
  const validateManualReference = async (referenceValue) => {
    if (!referenceValue || !referenceValue.trim()) {
      setIsValidReference(false);
      setReferrerInfo(null);
      return false;
    }

    setIsLoadingReferrer(true);

    try {
      console.log('Validating manually entered reference:', referenceValue.trim());
      const response = await apiServices.getAPI(`user/quick-info/${referenceValue.trim()}`);

      if (response.data && response.data.success) {
        const referrerData = response.data.data;
        setReferrerInfo(referrerData);
        console.log('Manual reference validated successfully:', referrerData);

        // Extract base_pay_amount from quick-info response if available (takes priority over genericamount)
        if (referrerData.base_pay_amount !== undefined && referrerData.base_pay_amount !== null) {
          const basePayAmount = referrerData.base_pay_amount;
          localStorage.setItem('base_pay_amount', basePayAmount.toString());
          localStorage.setItem('base_pay_amount_from_quickinfo', 'true'); // Flag to indicate quick-info was used
          const amountCents = Math.round(parseFloat(basePayAmount) * 100);
          setBaseMembershipAmountCents(amountCents);
          setPaymentAmountCents(amountCents);
          console.log('Base pay amount from quick-info (priority):', basePayAmount, '($' + (amountCents / 100).toFixed(2) + ')');
        }

        // Apply payment logic based on referrer type
        if (referrerData.usertype === 'Entity') {
          if (referrerData.category_status === 'UNPAID') {
            setRequiresPayment(false);
            console.log('Entity with UNPAID category - No payment required');
          } else {
            setRequiresPayment(true);
            console.log('Entity with PAID/no category - Payment required');
          }

          // Apply discount if available
          if (referrerData.discount_coupon_percent) {
            const discount = parseFloat(referrerData.discount_coupon_percent);
            setAppliedDiscount(discount);
            console.log('Discount applied:', discount + '%');
          }
        } else {
          setRequiresPayment(true);
          console.log('Non-entity referrer - Default payment flow');
        }

        // Mark reference as valid
        setIsValidReference(true);
        setIsNoReference(false);
        setIsReferenceFromLink(false);
        return true;

      } else {
        console.log('Manual reference validation failed - invalid reference');
        setIsValidReference(false);
        setReferrerInfo(null);
        await Swal.fire({
          title: 'Invalid Reference Number',
          text: 'The reference number you entered is invalid or inactive. Please check and try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return false;
      }
    } catch (error) {
      console.error('Error validating manual reference:', error);
      setIsValidReference(false);
      setReferrerInfo(null);
      await Swal.fire({
        title: 'Reference Validation Failed',
        text: 'The Reference Number you entered is not recognised. Please check and try again or continue without a referral.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return false;
    } finally {
      setIsLoadingReferrer(false);
    }
  };

  // Function to validate reference number from query parameters
  const validateReferenceFromQuery = () => {
    const urlParams = new URLSearchParams(location.search);

    let referenceNumber = null; // Peacekeeper ID for API
    let fullString = ''; // Full referral string for display

    // First try the old format (referencenumber parameter)
    referenceNumber = urlParams.get('referencenumber');

    if (referenceNumber) {
      // Old format: just the ID
      fullString = referenceNumber; // Same as ID in this case

      referenceNumber.split('-');
      if (referenceNumber.includes('-')) {
        const parts = referenceNumber.split('-');
        console.log('Split parts:', parts);

        if (parts.length >= 3) {
          const lastThreeElements = parts.slice(-3);
          referenceNumber = lastThreeElements.join('-');
        }
      }
    } else {
      // New format: extract from the first query parameter key
      const queryKeys = Array.from(urlParams.keys());
      if (queryKeys.length > 0) {
        const firstParamKey = queryKeys[0];
        console.log('First query parameter key (full referral string):', firstParamKey);

        // Save the full string for display
        fullString = firstParamKey;

        // Check if the key contains hyphens (new format)
        if (firstParamKey.includes('-')) {
          // Split by hyphens and take the last 3 elements as peacekeeper ID
          const parts = firstParamKey.split('-');
          console.log('Split parts:', parts);

          if (parts.length >= 3) {
            const lastThreeElements = parts.slice(-3);
            referenceNumber = lastThreeElements.join('-');
            console.log('Extracted peacekeeper ID from last 3 elements:', referenceNumber);
          }
        } else {
          // If no hyphens, use the key as is (fallback)
          referenceNumber = firstParamKey;
        }
      }
    }

    console.log('Full referral string:', fullString);
    console.log('Extracted peacekeeper ID for API:', referenceNumber);

    if (referenceNumber && referenceNumber.trim()) {
      // Valid reference number found
      const trimmedReference = referenceNumber.trim();
      setReference(trimmedReference); // Peacekeeper ID only (for API)
      setFullReferralString(fullString); // Full string (for display)
      setIsValidReference(true);
      setShowForm(true);
      setIsReferenceFromLink(true);
      setIsNoReference(false); // There IS a reference when ID is in URL
      console.log('Valid reference number found in URL, showing form and fetching referrer info for discount calculation');

      // Fetch referrer information to check payment requirements and get discount
      fetchReferrerInfo(trimmedReference);
    } else {
      // No valid reference number
      setIsValidReference(true);
      setIsNoReference(false)
      setIsReferenceFromLink(false);
      setShowForm(true);
      console.log('No valid reference number found, hiding form');
    }
  };

  // Fetch countries from API
  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await apiServices.getAPI('countries');
      if (response.data.countries && Array.isArray(response.data.countries)) {
        setCountries(response.data.countries);
      } else {
        console.error('Invalid countries data format:', response.data);
        setCountries([]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Run reference parsing exactly once on mount to avoid flicker loops
  useEffect(() => {
    validateReferenceFromQuery();
  }, []);

  // Keep payment amount in sync with referral link discount for UI copy
  useEffect(() => {
    // Get referral discount from referrerInfo - check multiple possible fields
    const referralDiscountPercent =
      parseFloat(referrerInfo?.referral_discount_amount) ||
      parseFloat(referrerInfo?.total_discount_percent) ||
      parseFloat(referrerInfo?.discount_coupon_percent) ||
      0;

    const discountCents = Math.round(
      baseMembershipAmountCents * (referralDiscountPercent / 100)
    );

    // Apply discount if:
    // 1. Reference is from link (URL) OR manually validated
    // 2. Reference exists and is not empty
    // 3. Discount percent is greater than 0
    if (
      (isReferenceFromLink || isValidReference) &&
      reference?.trim() &&
      referralDiscountPercent > 0
    ) {
      const calculatedAmount = Math.max(baseMembershipAmountCents - discountCents, 0);
      setPaymentAmountCents(calculatedAmount);
      console.log('Referral discount applied:', {
        baseAmount: baseMembershipAmountCents,
        discountPercent: referralDiscountPercent,
        discountCents: discountCents,
        finalAmount: calculatedAmount
      });
    } else {
      // No discount, use base amount
      setPaymentAmountCents(baseMembershipAmountCents);
      console.log('No referral discount, using base amount:', baseMembershipAmountCents);
    }
  }, [isReferenceFromLink, isValidReference, reference, referrerInfo?.referral_discount_amount, referrerInfo?.total_discount_percent, referrerInfo?.discount_coupon_percent, baseMembershipAmountCents]);

  // When reference becomes valid, load countries
  useEffect(() => {
    if (isValidReference) {
      fetchCountries();
    }
  }, [isValidReference]);

  // Add page load tracking
  useEffect(() => {
    console.log('=== PAGE LOADED/REFRESHED ===');
    console.log('Current form state:', { name, email, phonenumber, dob, country, reference, paymentComplete, paymentStatus, isSubmitting });

    // Hide any existing loader on page load/refresh
    setShowFullPageLoader(false);
    setLoaderMessage('');
  }, []);

  // Cleanup effect to ensure loader is hidden when component unmounts or state changes
  useEffect(() => {
    return () => {
      // Cleanup function to hide loader when component unmounts
      setShowFullPageLoader(false);
      setLoaderMessage('');
    };
  }, []);

  // Effect to monitor loader state and force hide if stuck
  useEffect(() => {
    if (showFullPageLoader) {
      const timeout = setTimeout(() => {
        console.log('Loader has been showing for 30 seconds, forcing hide');
        setShowFullPageLoader(false);
        setLoaderMessage('');
      }, 30000); // 30 seconds timeout

      return () => clearTimeout(timeout);
    }
  }, [showFullPageLoader]);

  // Credential check function
  const checkCredentials = async () => {
    try {
      setIsCheckingCredentials(true);
      setShowFullPageLoader(true);
      setLoaderMessage('Checking credentials...');

      const credentialCheckResponse = await apiServices.postAPI('check-credentials', {
        email: email,
        mobile: phonenumber
      });

      console.log('Credential check response:', credentialCheckResponse);

      const { results, summary } = credentialCheckResponse.data;

      // Validate API response structure
      if (!summary || typeof summary.emailExists !== 'boolean' || typeof summary.mobileExists !== 'boolean') {
        console.error('Invalid API response structure:', credentialCheckResponse.data);
        throw new Error('Invalid response from credential check API');
      }

      // Check if email or mobile already exists
      if (summary.emailExists || summary.mobileExists) {
        let errorMessage = 'Registration cannot proceed:\n\n';

        if (summary.emailExists) {
          errorMessage += `• Email "${email}" is already registered\n`;
        }

        if (summary.mobileExists) {
          errorMessage += `• Mobile number "${phonenumber}" is already registered\n`;
        }

        errorMessage += '\nPlease use different credentials or contact support if you believe this is an error.';

        // Hide loader
        setShowFullPageLoader(false);
        setLoaderMessage('');
        setIsCheckingCredentials(false);

        // Show error message
        await Swal.fire({
          title: '<span style="color: #d32f2f; font-size: 24px; font-weight: 600;">Credentials Already Exist</span>',
          html: `
             <div style="text-align: left; margin: 20px 0;">
               <p style="color: #333; font-size: 16px; margin-bottom: 15px; font-weight: 500;">
                 Registration cannot proceed because the following credentials are already registered:
               </p>
               <div style="background: #fff3e0; border-left: 4px solid #ff9800; padding: 15px; margin: 15px 0; border-radius: 4px;">
                 ${summary.emailExists ? `<p style="margin: 8px 0; color: #e65100;"><strong>📧 Email:</strong> "${email}"</p>` : ''}
                 ${summary.mobileExists ? `<p style="margin: 8px 0; color: #e65100;"><strong>📱 Mobile:</strong> "${phonenumber}"</p>` : ''}
               </div>
               <p style="color: #666; font-size: 14px; margin-top: 15px; line-height: 1.5;">
                 Please use different credentials or contact support if you believe this is an error.
               </p>
             </div>
           `,
          icon: 'warning',
          confirmButtonText: 'Got It',
          showCancelButton: false,
          customClass: {
            popup: 'enhanced-swal-popup',
            confirmButton: 'enhanced-swal-button enhanced-swal-button-primary',
            title: 'enhanced-swal-title',
            htmlContainer: 'enhanced-swal-html'
          },
          backdrop: 'rgba(0, 0, 0, 0.4)',
          allowOutsideClick: true,
          buttonsStyling: false,
          width: '500px',
          padding: '2rem'
        });

        return false; // Credentials exist, cannot proceed
      }

      // Credentials are available
      setShowFullPageLoader(false);
      setLoaderMessage('');
      setIsCheckingCredentials(false);
      return true; // Can proceed

    } catch (error) {
      console.error('Credential check error:', error);

      // Hide loader
      setShowFullPageLoader(false);
      setLoaderMessage('');
      setIsCheckingCredentials(false);

      // Show error message with more details
      let errorText = 'Unable to verify credentials. Please try again or contact support.';

      if (error.response?.status === 400) {
        errorText = 'Invalid request data. Please check your email and phone number format.';
      } else if (error.response?.status === 500) {
        errorText = 'Server error occurred. Please try again later.';
      } else if (error.response?.status === 404) {
        errorText = 'Credential check service not available. Please contact support.';
      }

      await Swal.fire({
        title: '<span style="color: #d32f2f; font-size: 24px; font-weight: 600;">❌ Credential Check Failed</span>',
        html: `
           <div style="text-align: center; margin: 20px 0;">
             <div style="background: #ffebee; border: 2px solid #f44336; border-radius: 8px; padding: 20px; margin: 15px 0;">
               <p style="color: #c62828; font-size: 16px; margin: 0; font-weight: 500;">
                 ${errorText}
               </p>
             </div>
             <p style="color: #666; font-size: 14px; margin-top: 15px; line-height: 1.5;">
               Please try again or contact our support team for assistance.
             </p>
           </div>
         `,
        icon: 'error',
        confirmButtonText: 'Try Again',
        showCancelButton: false,
        customClass: {
          popup: 'enhanced-swal-popup',
          confirmButton: 'enhanced-swal-button enhanced-swal-button-error',
          title: 'enhanced-swal-title',
          htmlContainer: 'enhanced-swal-html'
        },
        backdrop: 'rgba(0, 0, 0, 0.4)',
        allowOutsideClick: true,
        buttonsStyling: false,
        width: '500px',
        padding: '2rem'
      });

      return false; // Cannot proceed due to error
    }
  };

  // Profile image handling functions
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    setImageUploadError('');

    if (file) {
      // Validate file type - based on API documentation
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setImageUploadError('Only JPEG, PNG, GIF, and WebP images are allowed');
        return;
      }

      // Validate file size (max 5MB as per API docs)
      if (file.size > 5 * 1024 * 1024) {
        setImageUploadError('Image size must be less than 5MB');
        return;
      }

      // Validate minimum dimensions (recommended 50x50 as per API docs)
      const img = new Image();
      img.onload = () => {
        if (img.width < 50 || img.height < 50) {
          setImageUploadError('Image should be at least 50x50 pixels');
          return;
        }
        setProfileImage(file);

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          setProfileImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      };
      img.src = URL.createObjectURL(file);
    }
  };

  const removeProfileImage = () => {
    setProfileImage(null);
    setProfileImagePreview(null);
    setImageUploadError('');
    // Reset the file input
    const fileInput = document.getElementById('profile-image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Form validation
  const validateForm = () => {
    console.log('Validating form with data:', { name, email, phonenumber, dob, country, gender, reference });
    const errors = {};

    if (!name.trim()) {
      errors.name = 'Full Name is required';
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!phonenumber.trim()) {
      errors.phonenumber = 'Phone Number is required';
    } else if (phonenumber.length < 7) {
      errors.phonenumber = 'Phone Number must be at least 7 digits';
    } else if (!/^\d{7,15}$/.test(phonenumber)) {
      errors.phonenumber = 'Phone Number must contain only digits (7-15 digits)';
    }

    if (!dob) {
      errors.dob = 'Date of Birth is required';
    }

    if (!country) {
      errors.country = 'Country is required';
    }

    if (!gender) {
      errors.gender = 'Gender is required';
    }

    // Profile image validation (now required)
    if (!profileImage) {
      errors.profileImage = 'Profile image is required';
    } else if (imageUploadError) {
      errors.profileImage = imageUploadError;
    }

    // Second checkbox validation (mandatory)
    if (!agreeToTerms) {
      errors.agreeToTerms = 'You must agree to the terms to proceed';
    }

    console.log('Validation errors:', errors);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }

    switch (field) {
      case 'name':
        setName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'phonenumber':
        setPhoneNumber(value);
        break;
      case 'countryCode':
        setCountryCode(value);
        break;
      case 'dob':
        setDOB(value);
        break;
      case 'country':
        setCountry(value);
        break;
      case 'gender':
        setGender(value);
        break;
      case 'reference':
        setReference(value);
        break;
      default:
        break;
    }

    // Form data is not saved to localStorage anymore
  };

  const register = async (hideLoaderCallback) => {
    console.log('Email:', email);
    console.log('Password:', name);
    console.log('reference_by', reference)
    console.log('Current payment status:', paymentStatus)
    console.log('Profile image:', profileImage)

    let data = {
      "name": name,
      "phone": countryCode + phonenumber,
      "email": email,
      "password": null,
      "country": country,
      "gender": gender,
      "dob": dob,
      "discount_coupon_percent": discountPercent,
      "reference_by": reference,
      "payment_status": paymentStatus,
      "activestatus": "Y",  // New users are active by default
      "mobile_email_flag": mobileEmailVisibility ? "Y" : "N"
    }

    try {
      // Always use FormData since profile image is now mandatory
      const formData = new FormData();
      formData.append('profileImage', profileImage);

      // Append other fields to FormData
      Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
      });

      // Save UI logs before users API
      try {
        const logs = uiLogsRef.current.slice();
        if (email && logs.length > 0) {
          await apiServices.postAPI('save-ui-logs', { email: email.trim() || 'unknown', logs });
        }
      } catch (saveLogError) {
        console.error('Failed to save UI logs:', saveLogError);
      }

      console.log('Calling registration API with FormData (includes mandatory profile image):', formData);
      const res = await apiServices.postMultiPartFormDataAPI('users', formData);

      console.log("Registration API Response:", res);

      // Save UI logs after registration success
      try {
        const logs = uiLogsRef.current.slice();
        if (email && logs.length > 0) {
          await apiServices.postAPI('save-ui-logs', { email: email.trim() || 'unknown', logs });
        }
      } catch (saveLogError) {
        console.error('Failed to save UI logs:', saveLogError);
      }

      // Extract peacekeeper ID from response
      const peacekeeperId = res.data?.peacekeeper_id || 'N/A';
      const profileImageInfo = res.data?.profileImage;
      const profileImageStatus = profileImageInfo?.uploaded
        ? `uploaded successfully (${Math.round(profileImageInfo.size / 1024)}KB)`
        : profileImageInfo?.message || 'not provided';

      // localStorage is not used anymore

      // Reset form after successful registration
      resetForm();

      // IMPORTANT: Hide loader BEFORE showing SweetAlert dialogs
      // This ensures the loader disappears immediately after API success
      if (hideLoaderCallback) {
        hideLoaderCallback();
        console.log('Loader hidden via callback before showing SweetAlert');
      }

      // Show success message
      await Swal.fire({
        title: '<span style="color: #2e7d32; font-size: 24px; font-weight: 600;">🎉 Registration Successful!</span>',
        html: `
           <div style="text-align: center; margin: 20px 0; font-family: Arial, sans-serif;">
 
  <div style="background: #e8f5e8; border: 2px solid #4caf50; border-radius: 8px; padding: 20px; margin: 15px 0;">
 
    <p style="color: #2e7d32; font-size: 20px; font-weight: 700; margin: 0 0 10px;">
      Welcome to the I Am Peacekeeper Movement!
</p>
 
    <p style="color: #388e3c; font-size: 16px; margin: 0 0 15px;">
      Your account has been created successfully.
</p>
 
    <p style="color: #2e7d32; font-size: 15px; font-weight: 600; margin: 0 0 20px;">
      Your I Am Peacekeeper ID: <strong>${peacekeeperId}</strong>
</p>
 
    <div style="text-align: left; max-width: 500px; margin: 0 auto; font-size: 14px; color: #2e7d32; line-height: 1.6;">
 
      <p style="margin-bottom: 8px;"><strong>Next Steps:</strong></p>
 
      <ul style="padding-left: 18px; margin-top: 0;">
<li>Download the I Am Peacekeeper App from the App Store (<a href="${Configuration.mobileAppiOSUrl}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: underline;">${Configuration.mobileAppiOSUrl}</a>) or Play Store (<a href="${Configuration.mobileAppDownloadUrl}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: underline;">${Configuration.mobileAppDownloadUrl}</a>).</li>
<li>Use your registered email to sign in for the first time.</li>
</ul>
 
      <p style="margin: 12px 0 5px;"><strong>First-Time Login:</strong></p>
 
      <ul style="padding-left: 18px; margin-top: 0;">
<li>Enter your registered email</li>
<li>Receive and verify the OTP</li>
<li>Set your login password</li>
</ul>
 
      <p style="margin: 12px 0 5px;"><strong>Once Logged In:</strong></p>
 
      <ul style="padding-left: 18px; margin-top: 0;">
<li>Your digital ID card will be issued</li>
<li>You will have full access to the I Am Peacekeeper app</li>
<li>You can invite others via your link or QR and begin your Earn While You Serve journey</li>
</ul>
 
    </div>
 
    <p style="color: #555; font-size: 14px; margin-top: 20px;">
      You will now be redirected to the login page to access your account.
</p>
 
  </div>
</div>
         `,
        icon: 'success',
        confirmButtonText: 'Continue to Login',
        showCancelButton: false,
        customClass: {
          popup: 'enhanced-swal-popup',
          confirmButton: 'enhanced-swal-button enhanced-swal-button-success',
          title: 'enhanced-swal-title',
          htmlContainer: 'enhanced-swal-html'
        },
        backdrop: 'rgba(0, 0, 0, 0.4)',
        allowOutsideClick: true,
        buttonsStyling: false,
        width: '500px',
        padding: '2rem'
      });

      // Navigate to login page after successful registration
      navigate('/login');

      return res; // Return the registration response
    } catch (error) {
      console.error("Registration error:", error);
      setIsSubmitting(false);

      // Extract specific error message from API response
      let errorMessage = 'An error occurred during registration. Please try again.';
      let errorTitle = '❌ Registration Failed';

      if (error.response?.data?.error) {
        const apiError = error.response.data.error;

        if (apiError.includes('Missing required fields')) {
          errorTitle = '❌ Missing Information';
          errorMessage = 'Please fill in all required fields: name, phone, email, and country are required.';
        } else if (apiError.includes('Invalid country name')) {
          errorTitle = '❌ Invalid Country';
          errorMessage = 'Please select a valid country from the list.';
        } else if (apiError.includes('Only image files are allowed')) {
          errorTitle = '❌ Invalid File Type';
          errorMessage = 'Please select a valid image file (JPEG, PNG, GIF, or WebP).';
        } else {
          errorMessage = apiError;
        }
      } else if (error.response?.status === 400) {
        errorTitle = '❌ Invalid Data';
        errorMessage = 'Please check your information and try again.';
      } else if (error.response?.status === 500) {
        errorTitle = '❌ Server Error';
        errorMessage = 'Server error occurred. Please try again later.';
      }

      Swal.fire({
        title: `<span style="color: #d32f2f; font-size: 24px; font-weight: 600;">${errorTitle}</span>`,
        html: `
          <div style="text-align: center; margin: 20px 0;">
            <div style="background: #ffebee; border: 2px solid #f44336; border-radius: 8px; padding: 20px; margin: 15px 0;">
              <p style="color: #c62828; font-size: 16px; margin: 0; font-weight: 500;">
                ${errorMessage}
              </p>
            </div>
            <p style="color: #666; font-size: 14px; margin-top: 15px; line-height: 1.5;">
              If the problem persists, please contact our support team.
            </p>
          </div>
        `,
        icon: 'error',
        confirmButtonText: 'Try Again',
        showCancelButton: false,
        customClass: {
          popup: 'enhanced-swal-popup',
          confirmButton: 'enhanced-swal-button enhanced-swal-button-error',
          title: 'enhanced-swal-title',
          htmlContainer: 'enhanced-swal-html'
        },
        backdrop: 'rgba(0, 0, 0, 0.4)',
        allowOutsideClick: true,
        buttonsStyling: false,
        width: '500px',
        padding: '2rem'
      });

      // Re-throw error for auto-registration handling
      throw error;
    }
  }

  // Form reset function
  const resetForm = () => {
    setName('');
    setEmail('');
    setPhoneNumber('');
    setCountryCode('+91');
    setDOB('');
    setCountry('');
    setGender('');
    setReference('');
    setFullReferralString('');
    setProfileImage(null);
    setProfileImagePreview(null);
    setImageUploadError('');
    setMobileEmailVisibility(false);
    setPaymentComplete(false);
    setPaymentStatus('PENDING');
    setFormErrors({});
    setIsSubmitting(false);
    setIsReferenceFromLink(false);
    setIsNoReference(true);
    setIsValidReference(false);
    setReferrerInfo(null);
    setRequiresPayment(true);
    setAppliedDiscount(0);

    // Clear quick-info flag and revert to genericamount API flow
    localStorage.removeItem('base_pay_amount_from_quickinfo');
    console.log('Form reset, reverting to genericamount API flow');
    fetchBasePayAmountFromGeneric();

    console.log('Form reset completed');
  };

  return (
    <>
      {/* Enhanced SweetAlert Styles */}
      <style>
        {`
          .enhanced-swal-popup {
            border-radius: 16px !important;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
            font-family: DIN Pro Regular !important;
          }
          
          .enhanced-swal-title {
            margin-bottom: 0 !important;
            padding: 0 !important;
          }
          
          .enhanced-swal-html {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .enhanced-swal-button {
            border-radius: 25px !important;
            padding: 12px 30px !important;
            font-size: 16px !important;
            font-weight: 600 !important;
            text-transform: none !important;
            letter-spacing: 0.5px !important;
            transition: all 0.3s ease !important;
            border: none !important;
            cursor: pointer !important;
            min-width: 140px !important;
          }
          
          .enhanced-swal-button-primary {
            background: linear-gradient(135deg, #214F7D 0%, #3A8139 100%) !important;
            color: white !important;
            box-shadow: 0 4px 15px rgba(33, 79, 125, 0.3) !important;
          }
          
          .enhanced-swal-button-primary:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 20px rgba(33, 79, 125, 0.4) !important;
          }
          
          .enhanced-swal-button-success {
            background: linear-gradient(135deg, #2e7d32 0%, #4caf50 100%) !important;
            color: white !important;
            box-shadow: 0 4px 15px rgba(46, 125, 50, 0.3) !important;
          }
          
          .enhanced-swal-button-success:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 20px rgba(46, 125, 50, 0.4) !important;
          }
          
          .enhanced-swal-button-error {
            background: linear-gradient(135deg, #d32f2f 0%, #f44336 100%) !important;
            color: white !important;
            box-shadow: 0 4px 15px rgba(211, 47, 47, 0.3) !important;
          }
          
          .enhanced-swal-button-error:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 20px rgba(211, 47, 47, 0.4) !important;
          }
          
          .enhanced-swal-button-warning {
            background: linear-gradient(135deg, #f57c00 0%, #ff9800 100%) !important;
            color: white !important;
            box-shadow: 0 4px 15px rgba(245, 124, 0, 0.3) !important;
          }
          
          .enhanced-swal-button-warning:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 20px rgba(245, 124, 0, 0.4) !important;
          }
          
          /* SweetAlert2 specific overrides */
          .swal2-popup {
            border-radius: 16px !important;
          }
          
          .swal2-title {
            margin-bottom: 0 !important;
            padding: 0 !important;
          }
          
          .swal2-html-container {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .swal2-confirm {
            border-radius: 25px !important;
            padding: 12px 30px !important;
            font-size: 16px !important;
            font-weight: 600 !important;
            text-transform: none !important;
            letter-spacing: 0.5px !important;
            transition: all 0.3s ease !important;
            border: none !important;
            cursor: pointer !important;
            min-width: 140px !important;
          }
        `}
      </style>

      {/* Full Page Loader */}
      {showFullPageLoader && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            color: 'white'
          }}
        >
          <Box
            sx={{
              width: '80px',
              height: '80px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3A8139',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              mb: 2,
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
          <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
            {loaderMessage}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.8 }}>
            Please wait, this may take a few moments...
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
        pt={3} pb={3}
        className="LoginSection"
      >
        <Paper elevation={5} sx={{ p: { xs: 3, sm: 4 }, width: { xs: 320, sm: 600 }, textAlign: 'center', borderRadius: 2 }} className='UserSubSection'>

          {/* Show form only if we have a valid reference number */}
          {showForm ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  mb: 2
                }}
              >
                {/* Back to Home Button */}
                <Button
                  sx={{
                    position: 'absolute',
                    left: { xs: 0, sm: 0 },
                    top: { xs: -12, sm: 0 },
                    color: '#214F7D',
                    textTransform: 'none',
                    fontSize: { xs: '12px', sm: '14px' },
                    fontWeight: 500,
                    minWidth: 'auto',
                    px: { xs: 1, sm: 2 },
                    '&:hover': {
                      backgroundColor: 'rgba(33, 79, 125, 0.08)'
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0.5, sm: 1 },
                      '& svg': {
                        width: { xs: '18px', sm: '20px' },
                        height: { xs: '18px', sm: '20px' }
                      }
                    }
                  }}
                  onClick={() => navigate('/')}
                  startIcon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z" fill="currentColor" />
                    </svg>
                  }
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Back to Home
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Back
                  </Box>
                </Button>

                {/* Centered Logo */}
                <img src={imgUrl.peaceMovImg} alt="Logo" width="100" />
              </Box>

              {/* Heading */}
              <Typography variant="h6" fontWeight="400" mb={0} className='loginHeading'>
                REGISTER
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Register for I Am Peacekeeper only
              </Typography>
              <Typography variant="h6" fontWeight="400" className='secondaryHeading'>
                Complete your lifelong membership
              </Typography>
              {requiresPayment && (
                <>

                  {(isReferenceFromLink || isValidReference) &&
                    reference?.trim() &&
                    referrerInfo?.referral_discount_amount &&
                    parseFloat(referrerInfo.referral_discount_amount) > 0 &&
                    paymentAmountCents < baseMembershipAmountCents ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mb={1}
                      sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}
                    >
                      With the referral discount applied:{' '}
                      {parseFloat(referrerInfo.referral_discount_amount).toFixed(0)}% of ${(baseMembershipAmountCents / 100).toFixed(2)} = $
                      {(
                        (baseMembershipAmountCents / 100) *
                        (parseFloat(referrerInfo.referral_discount_amount) / 100)
                      ).toFixed(2)}
                      , so you pay{' '}
                      <strong style={{ color: 'green' }}>
                        ${(paymentAmountCents / 100).toFixed(2)}
                      </strong>
                    </Typography>
                  ) :
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mb={1}
                      className="green-title"
                    >
                      A one-time membership fee of{' '}
                      <strong style={{ color: 'green' }}>
                        ${baseMembershipAmountCents ? (baseMembershipAmountCents / 100).toFixed(2) : '21.00'}
                      </strong>
                    </Typography>}
                </>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left" }}>Full Name</Typography>
                  <TextField
                    fullWidth
                    label=""
                    placeholder='Enter Full Name'
                    type="text"
                    required
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 2 }}
                    value={name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left" }}>Date of Birth</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label=""
                      value={dob && dayjs(dob).isValid() ? dayjs(dob) : null}
                      onChange={(newValue) => {
                        if (newValue && dayjs(newValue).isValid()) {
                          const formattedDate = dayjs(newValue).format('YYYY-MM-DD');
                          handleFieldChange('dob', formattedDate);
                        } else {
                          handleFieldChange('dob', '');
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          margin: 'dense',
                          size: 'small',
                          placeholder: 'Select Date Of Birth',
                          required: true,
                          error: !!formErrors.dob,
                          helperText: formErrors.dob,
                          sx: { mb: 2 }
                        }
                      }}
                      maxDate={dayjs()}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left" }}>Email</Typography>
                  <TextField
                    fullWidth
                    label=""
                    placeholder='Enter Email'
                    type="email"
                    required
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 2 }}
                    value={email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left" }}>Phone Number</Typography>
                  <Box sx={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
                    {/* Country Code Dropdown */}
                    <Select
                      className="country-code-select"
                      classNamePrefix="country-code-select"
                      placeholder="Code"
                      isSearchable={true}
                      value={countryCodes.find(cc => cc.code === countryCode) || countryCodes.find(cc => cc.code === '+91')}
                      onChange={(selectedOption) => handleFieldChange('countryCode', selectedOption ? selectedOption.code : '+91')}
                      options={countryCodes}
                      getOptionLabel={(option) => option.code}
                      getOptionValue={(option) => option.code}
                      formatOptionLabel={(option) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img
                            src={`https://flagcdn.com/w20/${option.country}.png`}
                            alt={option.name}
                            style={{ width: '20px', height: '15px', objectFit: 'cover', borderRadius: '2px' }}
                          />
                          <span>{option.code}</span>
                        </Box>
                      )}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          minHeight: '40px',
                          width: '110px',
                          border: '1px solid #ccc',
                          borderRadius: '4px 0 0 4px',
                          borderRight: 'none',
                          boxShadow: state.isFocused ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
                          '&:hover': {
                            borderColor: '#999'
                          }
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#1976d2' : state.isFocused ? '#f5f5f5' : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          padding: '6px 5px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#1976d2' : '#f0f0f0'
                          }
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          marginTop: '4px',
                          width: '150px'
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          textAlign: 'left',
                          padding: '4px 0'
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#333',
                          textAlign: 'left',
                          fontSize: '14px'
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#999',
                          textAlign: 'left',
                          fontSize: '14px'
                        }),
                        noOptionsMessage: (provided) => ({
                          ...provided,
                          textAlign: 'left',
                          padding: '8px 12px',
                          fontSize: '14px'
                        }),
                        input: (provided) => ({
                          ...provided,
                          textAlign: 'left',
                          fontSize: '14px'
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          textAlign: 'left',
                          padding: '0 8px'
                        })
                      }}
                    />
                    {/* Phone Number Input */}
                    <TextField
                      fullWidth
                      label=""
                      type="tel"
                      required
                      variant="outlined"
                      margin="dense"
                      size="small"
                      placeholder="Enter phone number"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '0 4px 4px 0',
                          borderLeft: 'none'
                        }
                      }}
                      value={phonenumber}
                      onChange={(e) => handleFieldChange('phonenumber', e.target.value)}
                      error={!!formErrors.phonenumber}
                      helperText={formErrors.phonenumber}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left" }}>Nationality</Typography>
                  <Select
                    className="country-select"
                    classNamePrefix="country-select"
                    placeholder={loadingCountries ? "Loading countries..." : "Select a country"}
                    isDisabled={loadingCountries}
                    isLoading={loadingCountries}
                    isClearable={true}
                    isSearchable={true}
                    value={country ? { value: country, label: country } : null}
                    onChange={(selectedOption) => handleFieldChange('country', selectedOption ? selectedOption.value : '')}
                    options={countries.map(countryItem => ({
                      value: countryItem.countryname,
                      label: countryItem.countryname,
                      data: countryItem
                    }))}
                    noOptionsMessage={() => "No countries available"}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '40px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: state.isFocused ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
                        '&:hover': {
                          borderColor: '#999'
                        }
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#1976d2' : state.isFocused ? '#f5f5f5' : 'white',
                        color: state.isSelected ? 'white' : '#333',
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: state.isSelected ? '#1976d2' : '#f0f0f0'
                        }
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                        marginTop: '4px'
                      }),
                      menuList: (provided) => ({
                        ...provided,
                        textAlign: 'left',
                        padding: '4px 0'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#333',
                        textAlign: 'left'
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#999',
                        textAlign: 'left'
                      }),
                      noOptionsMessage: (provided) => ({
                        ...provided,
                        textAlign: 'left',
                        padding: '8px 12px'
                      }),
                      input: (provided) => ({
                        ...provided,
                        textAlign: 'left'
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        textAlign: 'left'
                      })
                    }}
                  />
                  {formErrors.country && (
                    <Typography color="error" sx={{ mt: 0.5, fontSize: '12px', textAlign: 'left' }}>
                      {formErrors.country}
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left" }}>Gender</Typography>
                  <TextField
                    fullWidth
                    select
                    label=""
                    placeholder="Select Gender"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 2 }}
                    value={gender}
                    onChange={(e) => handleFieldChange('gender', e.target.value)}
                    error={!!formErrors.gender}
                    helperText={formErrors.gender}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </TextField>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Typography sx={{ textAlign: "left" }}>Reference Number</Typography>
                  <TextField
                    fullWidth
                    label=""
                    placeholder='Enter Reference No'
                    type="text"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 2 }}
                    value={fullReferralString || reference}
                    onChange={async (e) => {
                      const value = e.target.value.toUpperCase();
                      setFullReferralString(value);
                      setReference(value);
                      handleFieldChange('reference', value);

                      // Validate when field length is 15 characters
                      if (value.trim().length >= 15) {
                        await validateManualReference(value.trim());
                      } else if (value.trim().length === 0) {
                        // Reset validation state when field is cleared
                        setIsValidReference(false);
                        setReferrerInfo(null);
                        setRequiresPayment(true);
                        setIsNoReference(true);
                        setIsReferenceFromLink(false);

                        // Clear quick-info flag and revert to genericamount API flow
                        localStorage.removeItem('base_pay_amount_from_quickinfo');
                        console.log('Reference cleared, reverting to genericamount API flow');
                        fetchBasePayAmountFromGeneric();
                      }
                    }}
                    inputProps={{
                      style: { textTransform: 'uppercase' }
                    }}
                    disabled={isReferenceFromLink}
                    helperText={isValidReference ? "Reference number validated" : isLoadingReferrer ? "Validating..." : ""}
                  />
                </Grid>
              </Grid>

              {/* Entity Referrer Information Display */}
              {referrerInfo && referrerInfo.usertype === 'Entity' && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    backgroundColor: '#f0f7ff',
                    border: '2px solid #214F7D',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(33, 79, 125, 0.1)'
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#214F7D',
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    🏢 Entity Referral Information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#666',
                            fontWeight: 500
                          }}
                        >
                          Entity Name:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            color: '#214F7D',
                            fontWeight: 600
                          }}
                        >
                          {referrerInfo.name || referrerInfo.username || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#666',
                            fontWeight: 500
                          }}
                        >
                          Category:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            color: '#3A8139',
                            fontWeight: 600
                          }}
                        >
                          {referrerInfo.categoryname || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Show discount or free registration info */}
                  {/* {referrerInfo.category_status === 'UNPAID' && (
                    <Box 
                      sx={{ 
                        mt: 2,
                        p: 1.5,
                        backgroundColor: '#e8f5e8',
                        borderRadius: 1,
                        border: '1px solid #4caf50'
                      }}
                    >
                      <Typography 
                        sx={{ 
                          fontSize: '0.875rem',
                          color: '#2e7d32',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        ✓ Free registration available through this entity
                      </Typography>
                    </Box>
                  )} */}
                </Box>
              )}

              {/* Profile Image Upload */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ textAlign: "left", mb: 1 }}>Profile Image</Typography>
                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#3A8139',
                      backgroundColor: '#f0f8f0'
                    }
                  }}
                  onClick={() => document.getElementById('profile-image-upload').click()}
                >
                  {profileImagePreview ? (
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={profileImagePreview}
                        alt="Profile Preview"
                        style={{
                          width: '120px',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '50%',
                          border: '3px solid #3A8139'
                        }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          minWidth: '30px',
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          p: 0
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeProfileImage();
                        }}
                      >
                        ×
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        📷 Click to upload profile image *
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Required: JPEG, PNG, GIF, WebP (Max 5MB, Min 50x50px)
                      </Typography>
                    </Box>
                  )}
                </Box>
                <input
                  id="profile-image-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                {imageUploadError && (
                  <Typography color="error" sx={{ mt: 0.5, fontSize: '12px', textAlign: 'left' }}>
                    {imageUploadError}
                  </Typography>
                )}
                {formErrors.profileImage && (
                  <Typography color="error" sx={{ mt: 0.5, fontSize: '12px', textAlign: 'left' }}>
                    {formErrors.profileImage}
                  </Typography>
                )}
              </Box>

              {/* Checkboxes */}
              <FormControlLabel
                sx={{ textAlign: "left" }}
                className='formCheckControl'
                control={
                  <Checkbox
                    checked={mobileEmailVisibility}
                    onChange={(e) => setMobileEmailVisibility(e.target.checked)}
                  />
                }
                label="Do you want the email and phone number to be visible on the Certificate? If Yes, Please tick the checkbox."
              />
              <FormControlLabel
                sx={{ textAlign: "left" }}
                className='formCheckControl'
                control={
                  <Checkbox
                    checked={agreeToTerms}
                    onChange={(e) => {
                      setAgreeToTerms(e.target.checked);
                      // Clear error when user checks the checkbox
                      if (formErrors.agreeToTerms) {
                        setFormErrors(prev => ({ ...prev, agreeToTerms: '' }));
                      }
                    }}
                    required
                  />
                }
                label="By clicking save, I express my solidarity to spread World Peace and express my willingness to receive promotional material from the I Am Peacekeeper Movement."
              />
              {formErrors.agreeToTerms && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: -1, mb: 1, ml: 4 }}>
                  {formErrors.agreeToTerms}
                </Typography>
              )}

              <>
                <Button
                  variant="contained"
                  sx={{
                    mt: 2,
                    background: 'linear-gradient(to right, #214F7D, #3A8139)',
                    color: 'white',
                    borderRadius: "50px"
                  }}
                  onClick={async () => {
                    if (validateForm()) {
                      // Check credentials before proceeding
                      const canProceed = await checkCredentials();
                      if (canProceed) {
                        // If reference is entered but not validated (e.g., not 15 chars yet), validate it now
                        if (reference && reference.trim() && !isValidReference && !isReferenceFromLink) {
                          // If reference is exactly 15 characters, validate it
                          if (reference.trim().length >= 15) {
                            const isValid = await validateManualReference(reference.trim());
                            if (!isValid) {
                              return; // Stop here if validation failed
                            }
                          } else if (reference.trim().length > 0) {
                            // Reference entered but not 15 characters yet
                            await Swal.fire({
                              title: 'Invalid Reference Number',
                              text: 'Reference number must be atleast 15 characters. Please check and try again.',
                              icon: 'error',
                              confirmButtonText: 'OK'
                            });
                            return; // Stop here
                          }
                        }

                        // If reference is entered but validation failed, don't proceed
                        if (reference && reference.trim() && !isValidReference && !isReferenceFromLink) {
                          await Swal.fire({
                            title: 'Reference Not Validated',
                            text: 'Please enter a valid reference number or continue without a referral.',
                            icon: 'warning',
                            confirmButtonText: 'OK'
                          });
                          return; // Stop here
                        }

                        // Check if payment is required based on referrer info
                        if (!requiresPayment) {
                          // Entity with UNPAID category - Register without payment
                          console.log('No payment required - registering directly');
                          await handleDirectRegistration();
                        } else {
                          // Default flow - Create payment intent and show payment modal
                          await createPaymentIntent();
                        }
                      }
                    } else {
                      console.log('Validation error');
                    }
                  }}
                  disabled={isSubmitting || isCheckingCredentials || isLoadingReferrer || isCreatingPaymentIntent}>
                  {isSubmitting ? 'Processing...' :
                    isCheckingCredentials ? 'Checking Credentials...' :
                      isLoadingReferrer ? 'Loading...' :
                        isCreatingPaymentIntent ? 'Preparing Payment...' :
                          !requiresPayment ? 'Complete Registration' : 'Proceed to Payment'}
                </Button>

                <Dialog open={open} onClose={() => {
                  console.log('Payment modal closing. Current form data:', { name, email, phonenumber, dob, country, reference });
                  setOpen(false);
                  // Hide loader if user manually closes the modal
                  setShowFullPageLoader(false);
                  setLoaderMessage('');
                  // Reset payment intent
                  setClientSecret('');
                  setStripeOptions(null);
                }} maxWidth="sm" fullWidth>
                  <DialogTitle>{`Complete Payment - $${(paymentAmountCents / 100).toFixed(2)}`}</DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Please complete your payment to proceed with registration.
                    </Typography>

                    {/* Payment Amount - Single Line */}
                    {(isReferenceFromLink || isValidReference) &&
                      reference?.trim() &&
                      referrerInfo?.referral_discount_amount &&
                      parseFloat(referrerInfo.referral_discount_amount) > 0 &&
                      paymentAmountCents < baseMembershipAmountCents ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={1}
                        sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}
                      >
                        With the referral discount applied:{' '}
                        {parseFloat(referrerInfo.referral_discount_amount).toFixed(0)}% of ${(baseMembershipAmountCents / 100).toFixed(2)} = $
                        {(
                          (baseMembershipAmountCents / 100) *
                          (parseFloat(referrerInfo.referral_discount_amount) / 100)
                        ).toFixed(2)}
                        , so you pay{' '}
                        <strong style={{ color: 'green' }}>
                          ${(paymentAmountCents / 100).toFixed(2)}
                        </strong>
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={1}
                        className="green-title"
                      >
                        A one-time membership fee of{' '}
                        <strong style={{ color: 'green' }}>
                          ${baseMembershipAmountCents ? (baseMembershipAmountCents / 100).toFixed(2) : '21.00'}
                        </strong>
                      </Typography>
                    )}

                    {stripeOptions && (
                      <Elements stripe={stripePromise} options={stripeOptions}>
                        <CheckoutForm
                          clientSecret={clientSecret}
                          onPaymentStart={() => {
                            console.log('Payment started - showing full page loader');
                            setShowFullPageLoader(true);
                            setLoaderMessage('Processing Payment...');
                          }}
                          onPaymentComplete={() => {
                            console.log('Payment processing complete');
                            // Don't hide loader yet - wait for success/failure
                          }}
                          onPaymentSuccess={async () => {
                            console.log('Payment success callback triggered');
                            console.log('Current form data before state update:', { name, email, phonenumber, dob, country, reference });

                            console.log('Setting payment complete to true');
                            setPaymentComplete(true);
                            setPaymentStatus('COMPLETED');
                            setOpen(false);

                            // Payment completion status is not saved to localStorage anymore

                            console.log('Payment modal closed, paymentComplete set to true');

                            // Add a small delay to ensure Stripe processing is complete
                            console.log('Waiting 2 seconds before auto-registration...');
                            await new Promise(resolve => setTimeout(resolve, 2000));

                            // Update loader message for registration
                            setLoaderMessage('Creating Your Account...');

                            // Automatically register the user after successful payment
                            console.log('Auto-registering user after payment success');
                            console.log('Form data before registration:', { name, email, phonenumber, dob, country, reference });
                            setIsSubmitting(true);

                            // Call registration API with proper error handling and explicit payment status
                            try {
                              // Double-check that payment status is set correctly
                              if (paymentStatus !== 'COMPLETED') {
                                console.warn('Payment status not COMPLETED, forcing update:', paymentStatus);
                                setPaymentStatus('COMPLETED');
                              }

                              // Ensure we use the correct payment status for registration
                              const registrationData = {
                                "name": name,
                                "phone": countryCode + phonenumber,
                                "email": email,
                                "password": null,
                                "country": country,
                                "gender": gender,
                                "dob": dob,
                                "discount_coupon_percent": discountPercent,
                                "reference_by": reference,
                                "payment_status": "COMPLETED", // Explicitly set to COMPLETED
                                "activestatus": "Y",  // New users are active by default
                                "mobile_email_flag": mobileEmailVisibility ? "Y" : "N"
                              };

                              // Add entity discount fields if referrer is Entity
                              if (referrerInfo && referrerInfo.usertype === 'Entity') {
                                registrationData.entity_discount_percent = referrerInfo.entity_discount_percent || "0";
                                registrationData.discount_coupon_percent = referrerInfo.discount_coupon_percent || "0";
                                console.log('Entity referral (paid) - Adding discount fields:', {
                                  entity_discount_percent: registrationData.entity_discount_percent,
                                  discount_coupon_percent: registrationData.discount_coupon_percent
                                });
                              }

                              console.log('Calling registration API with COMPLETED payment status:', registrationData);

                              // Always use FormData since profile image is now mandatory
                              const formData = new FormData();
                              formData.append('profileImage', profileImage);

                              // Append other fields to FormData
                              Object.keys(registrationData).forEach(key => {
                                formData.append(key, registrationData[key]);
                              });

                              // Save UI logs before users API
                              try {
                                const logs = uiLogsRef.current.slice();
                                if (email && logs.length > 0) {
                                  await apiServices.postAPI('save-ui-logs', { email: email.trim() || 'unknown', logs });
                                }
                              } catch (saveLogError) {
                                console.error('Failed to save UI logs:', saveLogError);
                              }

                              console.log('Calling registration API with FormData (includes mandatory profile image):', formData);
                              const res = await apiServices.postMultiPartFormDataAPI('users', formData);
                              console.log("Registration API Response:", res);

                              // Save UI logs after registration success
                              try {
                                const logs = uiLogsRef.current.slice();
                                if (email && logs.length > 0) {
                                  await apiServices.postAPI('save-ui-logs', { email: email.trim() || 'unknown', logs });
                                }
                              } catch (saveLogError) {
                                console.error('Failed to save UI logs:', saveLogError);
                              }

                              // Extract peacekeeper ID from response
                              const peacekeeperId = res.data?.peacekeeper_id || 'N/A';
                              const profileImageInfo = res.data?.profileImage;
                              const profileImageStatus = profileImageInfo?.uploaded
                                ? `uploaded successfully (${Math.round(profileImageInfo.size / 1024)}KB)`
                                : profileImageInfo?.message || 'not provided';

                              // localStorage is not used anymore

                              // Reset form after successful registration
                              resetForm();

                              // Hide loader
                              setShowFullPageLoader(false);
                              setLoaderMessage('');
                              console.log('Loader hidden after successful registration');

                              // Show success message
                              await Swal.fire({
                                title: '<span style="color: #2e7d32; font-size: 24px; font-weight: 600;">🎉 Registration Successful!</span>',
                                html: `
                                <div style="text-align: center; margin: 20px 0; font-family: Arial, sans-serif;">
 
  <div style="background: #e8f5e8; border: 2px solid #4caf50; border-radius: 8px; padding: 20px; margin: 15px 0;">
 
    <p style="color: #2e7d32; font-size: 20px; font-weight: 700; margin: 0 0 10px;">
      Welcome to the I Am Peacekeeper Movement!
</p>
 
    <p style="color: #388e3c; font-size: 16px; margin: 0 0 15px;">
      Your account has been created successfully.
</p>
 
    <p style="color: #2e7d32; font-size: 15px; font-weight: 600; margin: 0 0 20px;">
      Your I Am Peacekeeper ID: <strong>${peacekeeperId}</strong>
</p>
 
    <div style="text-align: left; max-width: 500px; margin: 0 auto; font-size: 14px; color: #2e7d32; line-height: 1.6;">
 
      <p style="margin-bottom: 8px;"><strong>Next Steps:</strong></p>
 
      <ul style="padding-left: 18px; margin-top: 0;">
<li>Download the I Am Peacekeeper App from the App Store (<a href="${Configuration.mobileAppiOSUrl}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: underline;">${Configuration.mobileAppiOSUrl}</a>) or Play Store (<a href="${Configuration.mobileAppDownloadUrl}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: underline;">${Configuration.mobileAppDownloadUrl}</a>).</li>
<li>Use your registered email to sign in for the first time.</li>
</ul>
 
      <p style="margin: 12px 0 5px;"><strong>First-Time Login:</strong></p>
 
      <ul style="padding-left: 18px; margin-top: 0;">
<li>Enter your registered email</li>
<li>Receive and verify the OTP</li>
<li>Set your login password</li>
</ul>
 
      <p style="margin: 12px 0 5px;"><strong>Once Logged In:</strong></p>
 
      <ul style="padding-left: 18px; margin-top: 0;">
<li>Your digital ID card will be issued</li>
<li>You will have full access to the I Am Peacekeeper app</li>
<li>You can invite others via your link or QR and begin your Earn While You Serve journey</li>
</ul>
 
    </div>
 
    <p style="color: #555; font-size: 14px; margin-top: 20px;">
      You will now be redirected to the login page to access your account.
</p>
 
  </div>
</div>
                              `,
                                icon: 'success',
                                confirmButtonText: 'Continue to Login',
                                showCancelButton: false,
                                customClass: {
                                  popup: 'enhanced-swal-popup',
                                  confirmButton: 'enhanced-swal-button enhanced-swal-button-success',
                                  title: 'enhanced-swal-title',
                                  htmlContainer: 'enhanced-swal-html'
                                },
                                backdrop: 'rgba(0, 0, 0, 0.4)',
                                allowOutsideClick: true,
                                buttonsStyling: false,
                                width: '500px',
                                padding: '2rem'
                              });

                              // Navigate to login page after successful registration
                              navigate('/login');

                            } catch (error) {
                              console.error('Auto-registration failed:', error);
                              setIsSubmitting(false);

                              // Hide full page loader on registration failure
                              setShowFullPageLoader(false);
                              setLoaderMessage('');

                              // Show error but keep form data for manual retry
                              Swal.fire({
                                title: '<span style="color: #f57c00; font-size: 24px; font-weight: 600;">⚠️ Auto-Registration Failed</span>',
                                html: `
                                <div style="text-align: center; margin: 20px 0;">
                                  <div style="background: #fff3e0; border: 2px solid #ff9800; border-radius: 8px; padding: 20px; margin: 15px 0;">
                                    <p style="color: #e65100; font-size: 16px; margin: 0; font-weight: 500;">
                                      Payment was successful but registration failed.
                                    </p>
                                    <p style="color: #f57c00; font-size: 14px; margin: 10px 0 0 0;">
                                      Please try registering manually or contact support.
                                    </p>
                                  </div>
                                  <p style="color: #666; font-size: 14px; margin-top: 15px; line-height: 1.5;">
                                    Your payment has been processed and will be refunded if needed.
                                  </p>
                                </div>
                              `,
                                icon: 'warning',
                                confirmButtonText: 'Try Again',
                                showCancelButton: false,
                                customClass: {
                                  popup: 'enhanced-swal-popup',
                                  confirmButton: 'enhanced-swal-button enhanced-swal-button-warning',
                                  title: 'enhanced-swal-title',
                                  htmlContainer: 'enhanced-swal-html'
                                },
                                backdrop: 'rgba(0, 0, 0, 0.4)',
                                allowOutsideClick: true,
                                buttonsStyling: false,
                                width: '500px',
                                padding: '2rem'
                              });
                            }
                          }}
                          onPaymentError={async (error) => {
                            // Hide full page loader on payment error
                            setShowFullPageLoader(false);
                            setLoaderMessage('');

                            // Call backend to cancel and log payment
                            try {
                              console.log('Calling cancel-and-log-payment on payment error:', { email });

                              await apiServices.postAPI('cancel-and-log-payment', {
                                email: email,
                                reason: error,
                              });

                              console.log('Cancel and log payment called successfully');
                            } catch (apiError) {
                              console.error('Error calling cancel-and-log-payment API:', apiError);
                              // Continue to refresh form even if API call fails
                            }

                            // Refresh entire Stripe form: clear current intent and create a new one
                            setClientSecret('');
                            setStripeOptions(null);
                            await createPaymentIntent();

                            Swal.fire({
                              title: '<span style="color: #d32f2f; font-size: 24px; font-weight: 600;">💳 Payment Failed</span>',
                              html: `
                              <div style="text-align: center; margin: 20px 0;">
                                <div style="background: #ffebee; border: 2px solid #f44336; border-radius: 8px; padding: 20px; margin: 15px 0;">
                                  <p style="color: #c62828; font-size: 16px; margin: 0; font-weight: 500;">
                                    ${error || 'Payment could not be processed. Please try again.'}
                                  </p>
                                </div>
                                <p style="color: #666; font-size: 14px; margin-top: 15px; line-height: 1.5;">
                                  Please check your payment details and try again.
                                </p>
                              </div>
                            `,
                              icon: 'error',
                              confirmButtonText: 'Try Again',
                              showCancelButton: false,
                              customClass: {
                                popup: 'enhanced-swal-popup',
                                confirmButton: 'enhanced-swal-button enhanced-swal-button-error',
                                title: 'enhanced-swal-title',
                                htmlContainer: 'enhanced-swal-html'
                              },
                              backdrop: 'rgba(0, 0, 0, 0.4)',
                              allowOutsideClick: true,
                              buttonsStyling: false,
                              width: '500px',
                              padding: '2rem',
                              didOpen: () => {
                                const container = document.querySelector('.swal2-container');
                                if (container) container.style.zIndex = 9999;
                              }
                            });
                          }}
                        />
                      </Elements>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => {
                      setOpen(false);
                      // Hide loader if user cancels
                      setShowFullPageLoader(false);
                      setLoaderMessage('');
                      // Reset payment intent
                      setClientSecret('');
                      setStripeOptions(null);
                    }}>Cancel</Button>
                  </DialogActions>
                </Dialog>
              </>

              {/* Auto-registration in progress indicator */}
              {isSubmitting && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>🔄 Processing Registration...</strong><br />
                    Please wait while we complete your registration.
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            /* Show error message if no valid reference number */
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  mb: 2
                }}
              >
                {/* Back to Home Button */}
                <Button
                  sx={{
                    position: 'absolute',
                    left: { xs: 0, sm: 0 },
                    top: { xs: -12, sm: 0 },
                    color: '#214F7D',
                    textTransform: 'none',
                    fontSize: { xs: '12px', sm: '14px' },
                    fontWeight: 500,
                    minWidth: 'auto',
                    px: { xs: 1, sm: 2 },
                    '&:hover': {
                      backgroundColor: 'rgba(33, 79, 125, 0.08)'
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0.5, sm: 1 },
                      '& svg': {
                        width: { xs: '18px', sm: '20px' },
                        height: { xs: '18px', sm: '20px' }
                      }
                    }
                  }}
                  onClick={() => navigate('/')}
                  startIcon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z" fill="currentColor" />
                    </svg>
                  }
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Back to Home
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Back
                  </Box>
                </Button>

                {/* Centered Logo */}
                <img src={imgUrl.peaceMovImg} alt="Logo" width="100" />
              </Box>

              {/* Error Message */}
              <Typography variant="h6" fontWeight="400" mb={2} className='loginHeading'>
                Invalid Referral Link
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                You need to have a valid referral link to access this registration form.
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Please contact your referrer or use the correct registration link.
              </Typography>

              <Button
                variant="contained"
                sx={{
                  background: 'linear-gradient(to right, #214F7D, #3A8139)',
                  color: 'white',
                  borderRadius: "50px"
                }}
                onClick={() => navigate("/")}
              >
                Go Back
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default Register;
